import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import CompetencyBar from './CompetencyBar';
import type { CompetencyResult } from '../../types/api';

interface AllKeywordsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competencies: (CompetencyResult | string)[];
  outcomesCount: number;
  onKeywordClick: (keyword: string, e: React.MouseEvent) => void;
}

const FILTER_KEYS = ['primary', 'practical', 'academic'] as const;
type FilterKey = (typeof FILTER_KEYS)[number];

const categoryDot: Record<FilterKey, string> = {
  primary: 'bg-ctu-tag-gray',
  practical: 'bg-ctu-tag-blue-text',
  academic: 'bg-ctu-tag-yellow-text',
};

function classify(comp: CompetencyResult | string): FilterKey {
  if (typeof comp === 'object') {
    if (comp.is_primary) return 'primary';
    if (comp.is_practical) return 'practical';
  }
  return 'academic';
}

function importanceOf(comp: CompetencyResult | string): number {
  return typeof comp === 'object' && typeof comp.importance === 'number' ? comp.importance : 0;
}

function keywordOf(comp: CompetencyResult | string): string {
  return typeof comp === 'string' ? comp : comp.keyword || '';
}

export default function AllKeywordsSheet({
  open,
  onOpenChange,
  competencies,
  outcomesCount,
  onKeywordClick,
}: AllKeywordsSheetProps) {
  const { t } = useTranslation('person');
  const [activeFilters, setActiveFilters] = useState<Set<FilterKey>>(new Set());

  const toggle = (k: FilterKey) =>
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });

  const isVisible = (k: FilterKey) =>
    activeFilters.size === 0 || activeFilters.has(k);

  const groups = useMemo(() => {
    const g: Record<FilterKey, (CompetencyResult | string)[]> = {
      primary: [],
      practical: [],
      academic: [],
    };
    for (const comp of competencies) {
      g[classify(comp)].push(comp);
    }
    const sortByImportance = (
      a: CompetencyResult | string,
      b: CompetencyResult | string,
    ) => {
      const ai = importanceOf(a);
      const bi = importanceOf(b);
      if (ai !== bi) return bi - ai;
      return keywordOf(a).localeCompare(keywordOf(b));
    };
    for (const k of FILTER_KEYS) g[k].sort(sortByImportance);
    return g;
  }, [competencies]);

  const labels: Record<FilterKey, string> = {
    primary: t('competenciesSection.legend.coreExpertise'),
    practical: t('competenciesSection.legend.practical'),
    academic: t('competenciesSection.legend.academic'),
  };

  const showLowData = outcomesCount > 0 && outcomesCount < 10;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg flex flex-col gap-0 p-0"
      >
        <SheetHeader className="px-6 pt-6 pb-4">
          <SheetTitle>{t('allKeywordsSheet.title')}</SheetTitle>
          <SheetDescription>{t('allKeywordsSheet.description')}</SheetDescription>
        </SheetHeader>

        <Separator />

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2 px-6 py-4">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted mr-1">
            {t('competenciesSection.legend.filterLabel')}
          </span>
          {FILTER_KEYS.map(k => {
            const count = groups[k].length;
            const disabled = count === 0;
            const muted = activeFilters.size > 0 && !activeFilters.has(k);
            const active = activeFilters.has(k);
            return (
              <button
                key={k}
                type="button"
                onClick={() => !disabled && toggle(k)}
                disabled={disabled}
                className={cn(
                  'group inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  'border-border bg-background text-ctu-text hover:bg-muted/60',
                  active && 'border-ctu-text bg-ctu-text text-white hover:bg-ctu-text [&>span:last-child]:text-white/60',
                  muted && 'opacity-40',
                  disabled && 'opacity-30 cursor-not-allowed pointer-events-none',
                )}
                title={
                  active
                    ? t('competenciesSection.legend.clickToHide')
                    : t('competenciesSection.legend.clickToShow')
                }
              >
                <span className={cn('size-2 rounded-full', categoryDot[k])} />
                <span>{labels[k]}</span>
                <span className="font-mono tabular-nums text-ctu-text-muted">{count}</span>
              </button>
            );
          })}
          {activeFilters.size > 0 && (
            <button
              type="button"
              onClick={() => setActiveFilters(new Set())}
              className="ml-1 text-xs text-ctu-text-muted hover:text-ctu-text underline"
            >
              {t('competenciesSection.legend.showAll')}
            </button>
          )}
        </div>

        {showLowData && (
          <div className="mx-6 mb-4 flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-xs leading-relaxed">
            <span className="text-base">⚠</span>
            <span>{t('competenciesSection.lowDataWarning', { count: outcomesCount })}</span>
          </div>
        )}

        <Separator />

        <ScrollArea className="flex-1">
          <div className="flex flex-col gap-8 px-6 py-6">
            {FILTER_KEYS.filter(isVisible).map(k => {
              const items = groups[k];
              if (items.length === 0) return null;
              return (
                <section key={k}>
                  <div className="mb-3 flex items-baseline gap-2">
                    <span className={cn('size-2 rounded-full translate-y-[-1px]', categoryDot[k])} />
                    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ctu-text-muted">
                      {labels[k]}
                    </span>
                    <span className="text-[11px] font-mono tabular-nums text-ctu-text-muted">
                      {items.length}
                    </span>
                  </div>
                  <ol className="m-0 flex flex-col divide-y divide-border list-none p-0">
                    {items.map((comp, idx) => {
                      const keyword = keywordOf(comp);
                      const importance = importanceOf(comp);
                      return (
                        <li key={`${k}-${keyword}-${idx}`} className="group">
                          <a
                            href={`/competencies/${encodeURIComponent(keyword)}`}
                            onClick={e => onKeywordClick(keyword, e)}
                            className="flex items-center gap-4 py-2.5 no-underline transition-colors hover:bg-muted/40 -mx-2 px-2 rounded"
                          >
                            <span className="w-6 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
                              {idx + 1}
                            </span>
                            <span className="flex-1 min-w-0 truncate text-sm font-medium text-ctu-text">
                              {keyword}
                            </span>
                            <CompetencyBar
                              importance={importance}
                              className="hidden sm:block w-20 shrink-0"
                            />
                            <span className="w-10 shrink-0 text-right text-xs font-mono tabular-nums text-ctu-text-muted">
                              {importance.toFixed(2)}
                            </span>
                            <ChevronRight
                              aria-hidden
                              className="h-3.5 w-3.5 shrink-0 text-ctu-text-muted opacity-0 transition-opacity group-hover:opacity-100"
                            />
                          </a>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              );
            })}

            {FILTER_KEYS.filter(isVisible).every(k => groups[k].length === 0) && (
              <p className="text-center text-sm text-ctu-text-muted py-10">
                {t('competenciesSection.noCompetencies')}
              </p>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
